import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Eye, MousePointerClick, Mail, TrendingUp, ExternalLink, FilePlus2 } from "lucide-react";
import api from "@/lib/api";

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-xl border border-ink-600 bg-ink-900 p-5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-slate-400 font-mono-label uppercase tracking-wider">{label}</span>
        <Icon size={16} className={accent ? "text-brand" : "text-slate-500"} />
      </div>
      <div className="font-display font-extrabold text-white text-3xl">{value?.toLocaleString?.() ?? value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-brand font-mono-label">loading dashboard...</div>;

  const ctaRate = d.total_views ? ((d.cta_clicks / d.total_views) * 100).toFixed(1) : "0.0";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="label-mono mb-1">Overview</div>
          <h1 className="font-display font-extrabold text-white text-3xl">Dashboard</h1>
        </div>
        <Link to="/admin/posts/new" data-testid="dashboard-new-post" className="inline-flex items-center gap-2 bg-brand text-brand-dark font-bold text-sm px-5 py-2.5 rounded-full hover:bg-brand-neon transition-colors"><FilePlus2 size={16} /> New Post</Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Total Articles" value={d.total_articles} icon={FileText} accent />
        <Stat label="Total Views" value={d.total_views} icon={Eye} accent />
        <Stat label="CTA Clicks" value={d.cta_clicks} icon={MousePointerClick} accent />
        <Stat label="Subscribers" value={d.subscribers} icon={Mail} accent />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Published" value={d.published} icon={FileText} />
        <Stat label="Drafts" value={d.drafts} icon={FileText} />
        <Stat label="Scheduled" value={d.scheduled} icon={FileText} />
        <Stat label="Outbound Clicks" value={d.outbound_clicks} icon={ExternalLink} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-ink-600 bg-ink-900 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display font-bold text-white text-lg flex items-center gap-2"><TrendingUp size={18} className="text-brand" /> Top Performing Articles</h2>
            <span className="text-xs text-slate-500 font-mono-label">CTA rate site wide: {ctaRate}%</span>
          </div>
          <div className="space-y-1">
            {d.top_articles.length === 0 && <p className="text-slate-500 text-sm">No published articles yet.</p>}
            {d.top_articles.map((a, i) => (
              <Link key={a.slug} to={`/article/${a.slug}`} target="_blank" className="flex items-center gap-4 py-3 border-b border-ink-700 last:border-0 hover:bg-ink-800/50 rounded px-2 transition-colors">
                <span className="font-display font-extrabold text-xl text-ink-600 w-7">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{a.title}</div>
                  <div className="text-xs text-slate-500 font-mono-label">{(a.category || "").replace(/-/g, " ")}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-brand">{a.views?.toLocaleString()}</div>
                  <div className="text-[10px] text-slate-500 font-mono-label">views</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-ink-600 bg-ink-900 p-6">
            <h2 className="font-display font-bold text-white text-lg mb-4">Traffic Sources</h2>
            <div className="space-y-3">
              {d.traffic_sources.length === 0 && <p className="text-slate-500 text-sm">No traffic data yet.</p>}
              {d.traffic_sources.map((s) => {
                const max = d.traffic_sources[0]?.c || 1;
                return (
                  <div key={s._id}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-300 capitalize">{s._id}</span><span className="text-slate-500">{s.c}</span></div>
                    <div className="h-1.5 bg-ink-700 rounded-full overflow-hidden"><div className="h-full bg-brand rounded-full" style={{ width: `${(s.c / max) * 100}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border border-ink-600 bg-ink-900 p-6">
            <h2 className="font-display font-bold text-white text-lg mb-4">Recent Activity</h2>
            <div className="space-y-3">
              {d.recent.map((r) => (
                <Link key={r.slug} to={`/admin/posts`} className="flex items-center gap-3 text-sm hover:text-brand transition-colors">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${r.status === "PUBLISHED" ? "bg-brand" : r.status === "DRAFT" ? "bg-slate-500" : "bg-amber-400"}`} />
                  <span className="text-slate-300 truncate flex-1">{r.title || "Untitled"}</span>
                  <span className="text-[10px] text-slate-600 font-mono-label">{r.status}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
